import {
  CoinName,
  ExchangeName,
  GeneralAmountType,
  GetGroupMainCoinsResponse,
} from "@shared/types";
import { round, toFixed } from "@shared/utils/numbers";
import { sum } from "lodash";
import { useContext, useMemo } from "react";
import { CoinGroupSignalsData } from "@shared/features/onboarding/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import { USD_PRECISION } from "@shared/features/onboarding/constants";
import { useBotFormAccountAmounts } from "@shared/features/bots/BotForm/common/hooks/useBotFormAccountAmounts";

const useAccountMainCoins = (
  mainCoinsToSignalsCount: GetGroupMainCoinsResponse | undefined,
  accountId?: number,
  accountExchange?: ExchangeName,
  skip?: boolean
) => {
  const { botAmountData, amountValue, amountType } =
    useContext(BotFormContext) || {};
  const { getMainCoinsAvailableAmountDataStatus, getMainCoinAmountData } =
    useBotFormAccountAmounts({
      account:
        accountId && accountExchange
          ? {
              client_id: accountId,
              exchange: accountExchange,
            }
          : undefined,
      skip,
    });

  const exchangeToMainCoinToLastMonthSignals = (exchange: ExchangeName) => {
    return mainCoinsToSignalsCount?.[exchange];
  };

  const getTotalLastMonthSignals = (exchange: ExchangeName) => {
    const exchangeMainCoinToLastMonthSignals =
      exchangeToMainCoinToLastMonthSignals(exchange);
    if (exchangeMainCoinToLastMonthSignals) {
      return sum(Object.values(exchangeMainCoinToLastMonthSignals));
    }
    return 0;
  };

  const getMainCoinSignals = (exchange: ExchangeName, coin: string) =>
    exchangeToMainCoinToLastMonthSignals(exchange)?.[coin];

  const getSignalsPercentage = (exchange: ExchangeName, coin: CoinName) => {
    const totalSignals = getTotalLastMonthSignals(exchange);
    const mainCoinSignals = getMainCoinSignals(exchange, coin);
    if (mainCoinSignals && totalSignals)
      return toFixed((mainCoinSignals / totalSignals) * 100, 2);
  };

  const getExchangeMainCoinsList = (exchange: ExchangeName) => {
    const exchangeMainCoinToLastMonthSignals =
      exchangeToMainCoinToLastMonthSignals(exchange);
    if (exchangeMainCoinToLastMonthSignals)
      return Object.keys(exchangeMainCoinToLastMonthSignals);
  };

  const getMainCoinsData = (
    exchange: ExchangeName,
    accountId: number
  ): CoinGroupSignalsData[] | undefined => {
    return getExchangeMainCoinsList(exchange)?.map((coin) => {
      const mainCoinAvailableAmountData = getMainCoinAmountData(
        accountId,
        coin
      );
      const mainCoinTotalAmountData = getMainCoinAmountData(
        accountId,
        coin,
        GeneralAmountType.TOTAL
      );
      const available = toFixed(
        mainCoinAvailableAmountData?.usd_amount || 0,
        USD_PRECISION
      );
      let selectedAmount = 0;
      if (!isSymbol(amountType) && !isSymbol(amountValue)) {
        selectedAmount = round(
          botAmountData?.getSelectedAmount?.(
            mainCoinAvailableAmountData,
            amountType,
            amountValue
          ) || 0,
          2
        );
      }
      let selectedPercent = 0;
      if (available) {
        selectedPercent = round((selectedAmount / available) * 100, 2);
      }

      return {
        coin: coin,
        numOfSignals: getMainCoinSignals(exchange, coin),
        signalsPercentage: getSignalsPercentage(exchange, coin),
        available,
        total: toFixed(mainCoinTotalAmountData?.usd_amount || 0, USD_PRECISION),
        selectedAmount,
        selectedPercent,
      };
    });
  };

  const getSortedMainCoins = (exchange: ExchangeName, accountId: number) => {
    const mainCoinsData = getMainCoinsData(exchange, accountId);
    if (mainCoinsData)
      return mainCoinsData.sort(
        (a, b) =>
          (b.numOfSignals || 0) - (a.numOfSignals || 0) ||
          (b.total || 0) - (a.total || 0)
      );
    return [];
  };

  const sortedMainCoinsData: CoinGroupSignalsData[] | undefined =
    useMemo(() => {
      if (accountExchange && accountId) {
        return getSortedMainCoins(accountExchange, accountId);
      }
    }, [
      accountId,
      accountExchange,
      mainCoinsToSignalsCount,
      getMainCoinsAvailableAmountDataStatus,
    ]);

  return {
    getTotalLastMonthSignals,
    sortedMainCoinsData,
    getMainCoinsAvailableAmountDataStatus,
  };
};

export default useAccountMainCoins;
